import { formatErrorRef } from "@atoms/atom-types";
import { translate } from "../i18n/TranslationBackendRegistry";
import type { NormalizedError } from "./normalizeError";
import { getGlobalNotifier, type NotifyAction } from "./notifier";

/**
 * What a user copies out of an error message, so a developer can find the
 * failed request. Built from the named fields of the normalised error only:
 * never the server message, a stack, variables or debug details.
 */
export type ErrorDetails = Readonly<{
  /** Reference of the failed request in the form people read: `K7QF-2M9X`. */
  reference?: string;
  code?: string;
  errorClass?: string;
  operation?: string;
  /** When the error was shown, ISO 8601 in UTC. */
  time: string;
  appVersion?: string;
  /** Path of the page, without query string and hash. */
  route?: string;
  sentryEventId?: string;
  /**
   * Id of an event the frontend reported for the same failure, when a screen
   * supplies one that differs from the backend's.
   */
  frontendSentryEventId?: string;
}>;

export type ErrorDetailsOptions = Readonly<{
  /**
   * A Sentry event id the caller holds for this failure (an error screen has
   * the id of the frontend capture). It becomes the event of the details when
   * the error carries none, and is listed next to it when the two differ.
   */
  sentryEventId?: string;
}>;

const MAX_FIELD_LENGTH = 64;

let appVersion: string | undefined;

/** Sets the version that "copy details" reports. Called once the frontend config is loaded. */
export function setErrorDetailsAppVersion(version: string | undefined): void {
  appVersion = version || undefined;
}

/** A value the server sent, cut to one short line before it is shown or copied. */
function toSingleLine(value: string | undefined): string | undefined {
  if (!value) {
    return undefined;
  }
  const firstLine = value.split("\n")[0]?.split("\r")[0] ?? "";
  const cut = firstLine.trim().slice(0, MAX_FIELD_LENGTH);
  return cut.length > 0 ? cut : undefined;
}

/** The reference of a failed request as people read it, or nothing. */
export function getDisplayedErrorRef(
  errorRef: string | undefined,
): string | undefined {
  const reference = toSingleLine(errorRef);
  return reference ? formatErrorRef(reference) : undefined;
}

export function buildErrorDetails(
  error: NormalizedError,
  now: Date = new Date(),
  options?: ErrorDetailsOptions,
): ErrorDetails {
  const ownEventId = toSingleLine(error.sentryEventId);
  const suppliedEventId = toSingleLine(options?.sentryEventId);
  return {
    reference: getDisplayedErrorRef(error.errorRef),
    code: toSingleLine(error.code),
    errorClass: toSingleLine(error.errorClass),
    operation: toSingleLine(error.operation),
    time: now.toISOString(),
    appVersion: toSingleLine(appVersion),
    route: globalThis.location?.pathname || undefined,
    sentryEventId: ownEventId ?? suppliedEventId,
    frontendSentryEventId:
      ownEventId && suppliedEventId && suppliedEventId !== ownEventId
        ? suppliedEventId
        : undefined,
  };
}

/**
 * The copied text. The labels are fixed and English: the text is read by a
 * developer and searched for in logs, whatever language the user works in.
 */
export function formatErrorDetails(details: ErrorDetails): string {
  const fields: ReadonlyArray<readonly [string, string | undefined]> = [
    ["Reference", details.reference],
    ["Code", details.code],
    ["Class", details.errorClass],
    ["Operation", details.operation],
    ["Time", details.time],
    ["App version", details.appVersion],
    ["Route", details.route],
    ["Sentry event", details.sentryEventId],
    ["Sentry event (frontend)", details.frontendSentryEventId],
  ];
  return fields
    .filter(([, value]) => value !== undefined)
    .map(([label, value]) => `${label}: ${value}`)
    .join("\n");
}

/**
 * Writes `text` to the clipboard. Resolves to whether it worked: the
 * clipboard API is missing on a page served without TLS and rejects without
 * focus or when a permission policy blocks it.
 */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  const clipboard = globalThis.navigator?.clipboard;
  if (!clipboard?.writeText) {
    return false;
  }
  try {
    await clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const COPIED_KEY = "error-details:copied";
const COPY_FAILED_KEY = "error-details:copy-failed";

/** Copies the details and tells the user whether it worked. */
export async function copyErrorDetails(details: ErrorDetails): Promise<void> {
  const copied = await copyTextToClipboard(formatErrorDetails(details));
  const notifier = getGlobalNotifier();
  if (copied) {
    notifier.dismiss(COPY_FAILED_KEY);
    notifier.notify(
      "success",
      translate("framework.errors.detailsCopied", "Details kopiert"),
      { key: COPIED_KEY, bypassDedupe: true },
    );
    return;
  }
  // The user can still read the reference and pass it on.
  notifier.notify(
    "warning",
    translate(
      "framework.errors.copyDetailsFailed",
      "Kopieren nicht möglich. Bitte notieren Sie die Referenz {{ref}}.",
      { ref: details.reference ?? "" },
    ),
    { key: COPY_FAILED_KEY, bypassDedupe: true, durationMs: 20_000 },
  );
}

/**
 * The "copy details" action of an error. It exists exactly when the backend
 * sent a class and a reference: every field of the details then comes from a
 * named wire field, and the reference finds the request.
 */
export function makeCopyErrorDetailsAction(
  error: NormalizedError,
  options?: ErrorDetailsOptions,
): NotifyAction | undefined {
  if (!error.errorClass || !getDisplayedErrorRef(error.errorRef)) {
    return undefined;
  }
  const details = buildErrorDetails(error, new Date(), options);
  return {
    label: translate("framework.errors.copyDetails", "Details kopieren"),
    kind: "link",
    keepOpen: true,
    onClick: () => {
      void copyErrorDetails(details);
    },
  };
}
